import { components as c } from "@/components";
import { type TrackingType } from "@patient-watch/types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { services as s } from "@/services";
import dayjs from "dayjs";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@patient-watch/ui/form";
import Stepper from "@/components/common/Stepper";
import { useInsertEvent } from "@/features/events/api/insertEvent";
import { EVENT_TYPES_WITH_SKIP } from "@/features/events/definitions/event-types";
import { useInsertSharedAttributes } from "@/features/shared_attributes/api/insertSharedAttributes";
import { useDiaryByIdQuery } from "@/features/diaries";
import { useEventNavigationStore } from "@/features/templates/hooks/useEventNavigationStore";
import { extractEventById } from "@/features/diaries/services/extractEventById";
import { useEffect } from "react";

const _newUuid = s.uuidv4();

const searchSchema = z.object({
    shared_attribute_id: z.string().optional(),
});

const EventsForm = () => {
    const { diaryId, eventId } = Route.useParams();
    const { shared_attribute_id: existingSharedAttributeId } = Route.useSearch();
    const { data: diary } = useDiaryByIdQuery(diaryId);
    const event = extractEventById(diary, eventId);
    const navigate = useNavigate();
    type EventSelection = TrackingType | "skip";
    const form = useForm<{ event: EventSelection }>({
        defaultValues: { event: event?.required_events?.[0] as EventSelection },
    });
    const { mutate: insertEvent } = useInsertEvent();
    const { mutate: insertSharedAttributes } = useInsertSharedAttributes();
    const selectedEvent = form.watch("event");

    const { steps, setEvent } = useEventNavigationStore();

    useEffect(() => {
        if (event) {
            form.reset({
                event: event?.required_events?.[0] as EventSelection,
            });
        }
    }, [event, form]);

    function onSubmit(data: { event: EventSelection }) {
        if (data.event === "skip") {
            // Don't add intervention - go back to patient view
            navigate({
                to: "/patients/$diaryId",
                params: { diaryId },
            });
            return;
        }

        setEvent(data.event);

        // If we have an existing shared_attribute_id (passed from tracker details), use it
        if (existingSharedAttributeId) {
            insertEvent(
                {
                    diary_id: diaryId,
                    event_id: eventId,
                    required_events: [data.event],
                    shared_attribute_id: existingSharedAttributeId,
                },
                {
                    onSuccess: () => {
                        navigate({
                            to: "/templates/diary/$diaryId/events/$eventId/details",
                            params: { diaryId, eventId },
                        });
                    },
                }
            );
        } else {
            // Create a new shared attribute
            insertSharedAttributes(
                {
                    start_date: dayjs().toISOString(),
                    reference_date: dayjs().toISOString(),
                    contact_id: diary?.shared_attributes.contact_id,
                },
                {
                    onSuccess: (sharedAttribute) => {
                        insertEvent(
                            {
                                diary_id: diaryId,
                                event_id: eventId,
                                required_events: [data.event],
                                shared_attribute_id: sharedAttribute.shared_attribute_id,
                            },
                            {
                                onSuccess: () => {
                                    navigate({
                                        to: "/templates/diary/$diaryId/events/$eventId/details",
                                        params: { diaryId, eventId },
                                    });
                                },
                            }
                        );
                    },
                }
            );
        }
    }

    return (
        <div className="space-y-8">
            <Stepper steps={steps} />
            <c.ui.Card>
                <c.ui.CardHeader>
                    <c.ui.CardTitle>Intervention</c.ui.CardTitle>
                </c.ui.CardHeader>
                <c.ui.CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="event"
                                rules={{
                                    required: "Please select an event type",
                                }}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <c.ui.RadioGroup
                                                value={field.value || ""}
                                                onValueChange={(val) => {
                                                    field.onChange(val);
                                                    setEvent(val);
                                                }}
                                                className="flex flex-col space-y-2"
                                            >
                                                {EVENT_TYPES_WITH_SKIP.map((event) => (
                                                    <div
                                                        key={event.value}
                                                        className="flex items-center space-x-2 hover:bg-accent p-2 rounded-md cursor-pointer"
                                                    >
                                                        <c.ui.RadioGroupItem value={event.value} id={event.value} />
                                                        <c.ui.Label htmlFor={event.value} className="cursor-pointer">
                                                            {event.label}
                                                        </c.ui.Label>
                                                    </div>
                                                ))}
                                            </c.ui.RadioGroup>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <c.ui.Button type="submit" disabled={!selectedEvent}>
                                Next
                            </c.ui.Button>
                        </form>
                    </Form>
                </c.ui.CardContent>
            </c.ui.Card>
        </div>
    );
};

export const Route = createFileRoute("/_app/templates/diary/$diaryId/events/$eventId/")({
    component: EventsForm,
    validateSearch: searchSchema,
});
